/**
 * WelcomeStep Component
 * First step in the setup wizard - introduces the platform and sets expectations
 */

import React from 'react';
import { Building2, FileText, Shield, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface WelcomeStepProps {
  onContinue: () => void;
}

export const WelcomeStep: React.FC<WelcomeStepProps> = ({ onContinue }) => {
  return (
    <div className="flex flex-col items-center text-center">
      {/* Swiss Shield Logo */}
      <div className="relative mb-6">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-terracotta-500 to-terracotta-700 flex items-center justify-center shadow-lg">
          <svg
            className="w-12 h-12 text-white"
            viewBox="0 0 64 64"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M32 2L8 12v16c0 14.4 9.92 27.84 24 31.2 14.08-3.36 24-16.8 24-31.2V12L32 2z" />
            <rect x="24" y="20" width="16" height="4" fill="#d97548" />
            <rect x="28" y="16" width="8" height="20" fill="#d97548" />
          </svg>
        </div>
      </div>

      <h2 className="text-2xl font-serif font-semibold text-espresso-900 dark:text-cream-100 mb-3">
        Welcome to LegalSyncUp
      </h2>

      <p className="text-espresso-500 dark:text-espresso-300 max-w-md mb-8">
        The Swiss legal entity management platform built for compliance.
        Let's set up your workspace in just a few steps.
      </p>

      {/* Feature highlights */}
      <div className="w-full max-w-md space-y-3 mb-8">
        <FeatureHighlight
          icon={<Building2 className="h-5 w-5" />}
          title="Swiss Entity Registry"
          description="Manage AG, GmbH, and all Swiss legal forms with UID validation"
        />
        <FeatureHighlight
          icon={<FileText className="h-5 w-5" />}
          title="Document Workflows"
          description="Create, approve, and publish legal documents with audit trails"
        />
        <FeatureHighlight
          icon={<Users className="h-5 w-5" />}
          title="Team Collaboration"
          description="Invite team members with role-based permissions"
        />
        <FeatureHighlight
          icon={<Shield className="h-5 w-5" />}
          title="Swiss Compliance"
          description="Built-in compliance for nDSG, GDPR, and cantonal requirements"
        />
      </div>

      {/* What we'll do */}
      <div className="w-full max-w-md bg-cream-100 dark:bg-espresso-700/30 rounded-xl p-4 mb-8">
        <h3 className="text-sm font-semibold text-espresso-900 dark:text-cream-100 mb-2">
          What we'll set up:
        </h3>
        <ul className="text-sm text-espresso-500 dark:text-espresso-300 space-y-1 text-left">
          <li className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-terracotta-100 dark:bg-terracotta-900/30 text-terracotta-600 flex items-center justify-center text-xs font-medium">1</span>
            Your profile information
          </li>
          <li className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-terracotta-100 dark:bg-terracotta-900/30 text-terracotta-600 flex items-center justify-center text-xs font-medium">2</span>
            Your first Swiss entity (optional)
          </li>
        </ul>
      </div>

      {/* CTA */}
      <Button
        onClick={onContinue}
        size="lg"
        className="bg-terracotta-600 hover:bg-terracotta-700 text-white px-8"
      >
        Get Started
      </Button>
    </div>
  );
};

interface FeatureHighlightProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const FeatureHighlight: React.FC<FeatureHighlightProps> = ({ icon, title, description }) => {
  return (
    <div className="flex items-start gap-3 text-left p-3 rounded-lg bg-cream-50 dark:bg-espresso-700/20 hover:bg-cream-100 dark:hover:bg-espresso-700/40 transition-colors">
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-terracotta-100 dark:bg-terracotta-900/30 flex items-center justify-center text-terracotta-600 dark:text-terracotta-400">
        {icon}
      </div>
      <div>
        <h4 className="text-sm font-semibold text-espresso-900 dark:text-cream-100">
          {title}
        </h4>
        <p className="text-xs text-espresso-500 dark:text-espresso-300">
          {description}
        </p>
      </div>
    </div>
  );
};

export default WelcomeStep;
